import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { ClipboardList, Plus, ChevronLeft, Search, Eye, Play, CheckCircle, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mfgAPI } from '../../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';

const STATUS_COLORS = {
  CREATED: 'bg-slate-100 text-slate-700',
  RELEASED: 'bg-orange-100 text-orange-700',
  PARTIALLY_CONFIRMED: 'bg-amber-100 text-amber-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  TECO: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-200 text-gray-600',
};

const ProductionOrderList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('production_order');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await mfgAPI.getProductionOrders(statusFilter ? { status: statusFilter } : {});
      setOrders(res.data);
    } catch (e) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, msg) => {
    try { await fn(id); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const filtered = orders.filter((o) =>
    o.order_id.toLowerCase().includes(search.toLowerCase()) ||
    o.material_id.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="production-orders-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing')}><ChevronLeft className="h-5 w-5" /></Button>
            <div className="p-2 bg-orange-600 rounded-lg"><ClipboardList className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Production Orders</h1>
              <p className="text-sm text-slate-500">Shop-floor execution documents</p>
            </div>
          </div>
          {writable && (
            <Button onClick={() => navigate('/manufacturing/production-orders/create')} className="bg-orange-600 hover:bg-orange-700" data-testid="create-production-order-btn">
              <Plus className="h-4 w-4 mr-2" /> New Order
            </Button>
          )}
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search order or material…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50" data-testid="order-search-input" />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px] bg-slate-50" data-testid="order-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.keys(STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold">Order</TableHead>
                  <TableHead className="font-semibold">Material</TableHead>
                  <TableHead className="font-semibold">Plant</TableHead>
                  <TableHead className="font-semibold text-right">Qty</TableHead>
                  <TableHead className="font-semibold text-right">Confirmed</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No production orders</TableCell></TableRow>
                ) : filtered.map((o) => (
                  <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`} className="hover:bg-orange-50/40 cursor-pointer" onClick={() => navigate(`/manufacturing/production-orders/${o.order_id}`)}>
                    <TableCell className="font-mono font-medium text-orange-600">{o.order_id}</TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{o.material_id}</p>
                      {o.material_description && <p className="text-xs text-slate-500 truncate max-w-[200px]">{o.material_description}</p>}
                    </TableCell>
                    <TableCell>{o.plant_id}</TableCell>
                    <TableCell className="text-right">{o.order_quantity} {o.uom}</TableCell>
                    <TableCell className="text-right">{o.confirmed_quantity}</TableCell>
                    <TableCell><Badge className={`${STATUS_COLORS[o.system_status]} border-0`}>{o.system_status}</Badge></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/manufacturing/production-orders/${o.order_id}`)} title="View"><Eye className="h-4 w-4" /></Button>
                      {writable && o.system_status === 'CREATED' && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-orange-600" onClick={() => act(mfgAPI.releaseProductionOrder, o.order_id, 'Released')} title="Release" data-testid={`release-${o.order_id}`}><Play className="h-4 w-4" /></Button>
                      )}
                      {writable && ['RELEASED', 'PARTIALLY_CONFIRMED', 'CONFIRMED'].includes(o.system_status) && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => act(mfgAPI.tecoProductionOrder, o.order_id, 'Technically completed')} title="TECO"><CheckCircle className="h-4 w-4" /></Button>
                      )}
                      {writable && o.system_status === 'TECO' && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-600" onClick={() => act(mfgAPI.closeProductionOrder, o.order_id, 'Closed')} title="Close"><Lock className="h-4 w-4" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ProductionOrderList;
